/** @jsx isolateComponent */

import { css } from '@emotion/core';

import {
  Button,
  ButtonGroup,
  Colors,
  Icon,
  Link,
  Spacer,
  Text,
} from '@zapier/design-system';

import { isolateComponent } from '@zapier/style-encapsulation';

import {
  useSendFullStoryEvent,
  useTrackEvent,
  useTrackingContext,
} from '@zapier/toolbox-tracking';

import { useSize } from '@zapier/upsells/src/hooks';
import { Account, Currency, Plan, Size } from '@zapier/upsells/src/types';

import { PlanCard } from './PlanCard';

const bodyTextColor = 'gray';

const styles = {
  orderedList: css({
    li: css`
      color: ${Colors.gray};
      display: list-item;
      list-style: decimal;
      margin-bottom: 10px;
    `,
    marginLeft: 20,
  }),
  planCardWrapper: (size: Size) => css`
    align-items: center;
    display: flex;
    flex-direction: ${size === 'small' ? 'column' : 'row'};
    padding: 20px;
    width: 100%;
  `,
  scrollWrapper: (size: Size) =>
    css({
      maxHeight:
        size === 'small'
          ? 'calc(100vh - 285px)'
          : `min(calc(100vh - 80px - 60px), 600px)`,
      overflowY: 'auto',
    }),
};

type Props = {
  account: Account;
  autoUpgradeToPlan: Plan;
  currency: Currency;
  onAcceptClick: () => void;
};

export const AutoUpgradeOptInView = ({
  account,
  autoUpgradeToPlan,
  currency,
  onAcceptClick,
}: Props) => {
  const { trackEvent } = useTrackingContext();
  const size = useSize();

  useSendFullStoryEvent('Viewed Auto Upgrade Modal - Opt-in View');
  useTrackEvent('auto_upgrade_modal_optin_view');

  return (
    <div>
      <div css={styles.scrollWrapper(size)}>
        <Text tag="h1" type="sectionHeader3">
          New: Try automatic task upgrade
        </Text>

        <Spacer height={10} />

        <Text color={bodyTextColor} tag="p" type="paragraph3">
          We're giving you early access to a beta feature where we'll
          automatically upgrade your task tier if you hit your current task
          limit.
        </Text>

        <Spacer height={20} />

        <Text tag="h2" type="paragraph1Bold">
          How it works
        </Text>

        <Spacer height={15} />

        <Text color={bodyTextColor} tag="span" type="paragraph3">
          <ol css={styles.orderedList}>
            <li>
              Today, if you reach your task limit, we{' '}
              <Link
                href="/help/manage/history/view-and-manage-your-zap-history"
                target="_blank"
              >
                hold your new Zap runs
              </Link>{' '}
              until you manually adjust your plan.
            </li>
            <li>
              If you opt in to the beta, we'll automatically upgrade you to the
              next task tier if you run out of tasks on your current plan.
            </li>
            <li>
              You'll be{' '}
              <Link href="/l/automatic-task-upgrades" target="_blank">
                billed at your new rate
              </Link>{' '}
              each month and you can change your plan any time from your{' '}
              <Link href="/app/settings/billing" target="_blank">
                Billing and Usage
              </Link>{' '}
              settings.
            </li>
          </ol>
        </Text>

        <div css={styles.planCardWrapper(size)}>
          <PlanCard
            currency={currency}
            plan={account.plan}
            title="Your current plan"
          />

          <Icon
            color="gray"
            name={size === 'small' ? 'arrowBigDown' : 'arrowBigRight'}
            size={60}
          />

          <PlanCard
            currency={currency}
            hasBorder={true}
            plan={autoUpgradeToPlan}
            title="Your next plan"
          />
        </div>

        <Spacer height={10} />

        <Text color={bodyTextColor} type="smallPrint1">
          By opting in, you agree to upgrade your task tier automatically.
          You'll upgrade your plan to the {autoUpgradeToPlan.type} plan with{' '}
          {Number(autoUpgradeToPlan.tasks).toLocaleString()} tasks going forward
          if you run out of tasks.
        </Text>
      </div>

      <Spacer height={30} />

      <ButtonGroup
        columnSize={size === 'small' ? '100%' : '330px'}
        justify="center"
      >
        <Button
          color="secondary"
          href="/l/automatic-task-upgrades"
          target="_blank"
        >
          Learn more
        </Button>

        <Button
          hasShadow={true}
          onClick={() => {
            trackEvent('auto_upgrade_modal_optin_view_optin_button');
            onAcceptClick();
          }}
        >
          {size === 'small' ? 'Opt in' : 'Opt in to automatic task upgrade'}
        </Button>
      </ButtonGroup>
    </div>
  );
};
